import { useState, useEffect, useCallback } from 'react';
import { Plus, Eye, CheckCircle, XCircle, Upload, ChevronDown, ChevronUp, Calendar, X, Trash2 } from 'lucide-react';
import { publicidadService, publicidadConfigService, storageService, editionService } from '../../../services/api';
import InfoTip from './InfoTip';
import styles from './PublicidadTab.module.css';

const ESTADOS = ['borrador','pendiente_material','en_revision','aprobada','activa','pausada','vencida','cancelada'];
const ESTADO_COLORS = {
  borrador: styles.badgeBorrador, aprobada: styles.badgeAprobado,
  activa: styles.badgeActiva, en_revision: styles.badgePendiente,
  pendiente_material: styles.badgePendiente,
  cancelada: styles.badgeRechazado, pausada: styles.badgeInactivo,
  vencida: styles.badgeInactivo,
  // materiales
  aprobado: styles.badgeAprobado, pendiente: styles.badgePendiente,
  rechazado: styles.badgeRechazado, archivado: styles.badgeInactivo,
};

// ─── Fila de pauta en la tabla ────────────────────────────────
function PautaRow({ pauta, onVerDetalle, onEliminar }) {
  return (
    <tr>
      <td><strong>#{pauta.id}</strong></td>
      <td>{pauta.anuncianteNombre}</td>
      <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pauta.formatoNombre}</td>
      <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pauta.ubicacionNombre}</td>
      <td>
        {pauta.modalidadVigencia === 'ediciones' ? `${pauta.numEdiciones} edic.`
         : pauta.modalidadVigencia === 'fechas' ? `${pauta.fechaInicio} → ${pauta.fechaFin || '∞'}`
         : 'Indefinida'}
      </td>
      <td>{pauta.totalCop ? `$${pauta.totalCop.toLocaleString('es-CO')}` : '—'}</td>
      <td><span className={`${styles.badge} ${ESTADO_COLORS[pauta.estado] || ''}`}>{pauta.estado}</span></td>
      <td>
        <div className={styles.actions}>
          <button className={styles.btnIcon} onClick={() => onVerDetalle(pauta.id)}><Eye size={12} /></button>
          {pauta.estado === 'borrador' && (
            <button className={`${styles.btnIcon} ${styles.danger}`} title='Eliminar borrador'
              onClick={() => onEliminar(pauta.id)}>
              <XCircle size={12} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Historial de material archivado ─────────────────────────
function HistorialMaterial({ materiales, pautaId, onReactivar }) {
  const [open, setOpen] = useState(false);
  const [reactivando, setReactivando] = useState(null);

  const reactivar = async (m) => {
    if (!window.confirm(`¿Reactivar "${m.nombre}"? Quedará en estado "pendiente" y deberás aprobarlo nuevamente.`)) return;
    setReactivando(m.id);
    try {
      await publicidadService.revisarMaterial(m.id, { estadoRevision: 'pendiente', observacionesRevision: 'Reactivado desde historial' });
      await onReactivar();
    } catch(e) { alert('Error: ' + e.message); }
    setReactivando(null);
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid #e8e8e4', borderRadius: 6 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', background: '#f9f9f7', border: 'none',
        cursor: 'pointer', borderRadius: open ? '6px 6px 0 0' : 6,
        borderBottom: open ? '1px solid #e8e8e4' : 'none',
        fontSize: 13, color: '#555', fontWeight: 600,
      }}>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        📦 Historial de material archivado ({materiales.length})
        <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400, marginLeft: 4 }}>
          — puede reutilizarse en cualquier momento
        </span>
      </button>

      {open && (
        <div style={{ padding: 12 }}>
          <table className={styles.table}>
            <thead>
              <tr><th>Nombre</th><th>Tipo</th><th>Archivado</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {materiales.map(m => (
                <tr key={m.id} style={{ opacity: 0.7 }}>
                  <td>{m.nombre}</td>
                  <td>{m.tipoArchivo}</td>
                  <td style={{ fontSize: 12, color: '#888' }}>
                    {m.revisadoEn ? new Date(m.revisadoEn).toLocaleDateString('es-CO') : '—'}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <a href={m.urlStorage} target='_blank' rel='noreferrer' className={styles.btnIcon}
                        title='Ver el archivo original'>Ver</a>
                      <button className={styles.btnIcon}
                        title='Reactivar este material — quedará pendiente de aprobación'
                        disabled={reactivando === m.id}
                        onClick={() => reactivar(m)}
                        style={{ color: '#1B4332', borderColor: '#1B4332' }}>
                        {reactivando === m.id ? '...' : '↺ Reactivar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: '#aaa', margin: '8px 0 0' }}>
            Al reactivar, el material vuelve a estado "pendiente" y debe aprobarse antes de poder marcarse como principal.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Detalle expandible de pauta ──────────────────────────────
function DetallePauta({ pautaId, onClose, config = {} }) {
  const [pauta, setPauta]       = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [obs, setObs]           = useState('');
  const [saving, setSaving]     = useState(false);
  const [editando, setEditando] = useState(false);
  const [formEdit, setFormEdit] = useState({});
  const [duplicando, setDuplicando] = useState(false);
  const [anuncianteDup, setAnuncianteDup] = useState('');
  const [anunciantes, setAnunciantes] = useState([]);
  const [materialNombre, setMaterialNombre]   = useState('');
  const [materialTipo, setMaterialTipo]       = useState('imagen');
  const [materialUrlDestino, setMaterialUrlDestino] = useState('');
  const [materialUrl, setMaterialUrl]         = useState('');
  const [archivoFile, setArchivoFile]         = useState(null);
  const [uploadProgress, setUploadProgress]   = useState(0);
  const [ediciones, setEdiciones]             = useState([]);
  const [edicionSel, setEdicionSel]           = useState('');
  const [programando, setProgramando]         = useState(false);
  const [edicionesAsignadas, setEdicionesAsignadas] = useState([]);

  const reloadAsignadas = useCallback(() =>
    publicidadService.getEdicionesDePauta(pautaId)
      .then(setEdicionesAsignadas).catch(() => setEdicionesAsignadas([])),
    [pautaId]);

  const reload = useCallback(() =>
    publicidadService.getPauta(pautaId).then(p => { setPauta(p); setNuevoEstado(p.estado); }),
    [pautaId]);

  useEffect(() => {
    reload();
    reloadAsignadas();
    editionService.getAll?.().then(setEdiciones).catch(() => {});
    publicidadService.getAnunciantes().then(setAnunciantes).catch(() => {});
  }, [reload, reloadAsignadas]);

  if (!pauta) return <div className={styles.empty}>Cargando…</div>;

  const cambiarEstado = async () => {
    setSaving(true);
    const r = await publicidadService.cambiarEstado(pautaId, { estado: nuevoEstado, observaciones: obs });
    setPauta(r); setSaving(false);
  };

  const subirMaterial = async () => {
    if (!materialNombre.trim()) return alert('Escribe un nombre para el material.');
    setSaving(true);
    try {
      let urlFinal = materialUrl;
      if (archivoFile && ['imagen','video','audio'].includes(materialTipo)) {
        setUploadProgress(20);
        const up = await storageService.uploadMaterialPublicidad(pautaId, archivoFile, materialTipo);
        urlFinal = up.url || up.gsUri;
        setUploadProgress(80);
      }
      if (!urlFinal) { setSaving(false); setUploadProgress(0); return alert('Selecciona un archivo.'); }
      await publicidadService.subirMaterial(pautaId, {
        nombre: materialNombre, tipoArchivo: materialTipo,
        urlStorage: urlFinal, urlDestino: materialUrlDestino || null,
      });
      await reload();
      setMaterialNombre(''); setMaterialUrl(''); setMaterialUrlDestino(''); setArchivoFile(null);
    } catch(e) { alert('Error: ' + e.message); }
    setSaving(false); setUploadProgress(0);
  };

  const fmt = v => v != null ? `$${Number(v).toLocaleString('es-CO')}` : '—';

  // Specs técnicas según tipo de material y formato de la pauta
  const specsFormato = () => {
    const f = pauta.formato;
    if (!f) return null;
    const lines = [];
    if (f.anchoPx && f.altoPx) lines.push(`Dimensiones exactas: ${f.anchoPx} × ${f.altoPx} px`);
    if (f.tiposArchivo) lines.push(`Formatos admitidos: ${f.tiposArchivo}`);
    if (f.resolucionMinDpi) lines.push(`Resolución mínima: ${f.resolucionMinDpi} DPI`);
    else if (f.canal === 'digital' || f.canal === 'revista') lines.push(`Resolución recomendada: 72 DPI (estándar pantalla). Mayor DPI aumenta el peso sin mejorar la calidad visual`);
    if (f.perfilColor) lines.push(`Perfil de color: ${f.perfilColor}`);
    const limites = { imagen: 'Peso máximo: 150 KB', video: 'Peso máximo: 200 MB', audio: 'Peso máximo: 50 MB' };
    if (limites[materialTipo]) lines.push(limites[materialTipo]);
    if (materialTipo === 'imagen') lines.push('GIF animado: máx 3 ciclos, 15 segundos');
    if (materialTipo === 'video') lines.push('Codificación: H.264. Duración: máx 30 seg');
    if (materialTipo === 'audio') lines.push('Calidad mínima: 128 kbps. Duración: máx 30 seg');
    return lines.length ? lines : null;
  };

  // Precio calculado según vigencia por fechas (días)
  const precioCalculado = () => {
    if (!pauta.totalCop && pauta.modalidadVigencia === 'fechas' && pauta.fechaInicio && pauta.fechaFin) {
      // El precio base está en 0 porque no se usó el cotizador al crear.
      // Mostrar aviso útil en vez de $0.
      const dias = Math.ceil((new Date(pauta.fechaFin) - new Date(pauta.fechaInicio)) / (1000*60*60*24)) + 1;
      return { dias, sinPrecio: true };
    }
    if (pauta.modalidadVigencia === 'fechas' && pauta.fechaInicio && pauta.fechaFin) {
      const dias = Math.ceil((new Date(pauta.fechaFin) - new Date(pauta.fechaInicio)) / (1000*60*60*24)) + 1;
      return { dias, sinPrecio: false };
    }
    return null;
  };

  return (
    <div style={{ background:'#f9f9f9', border:'1px solid #ddd', borderRadius:8, padding:20, marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 style={{ margin:0, fontSize:15 }}>Pauta #{pauta.id} — {pauta.anunciante?.razonSocial}</h3>
        <div style={{ display:'flex', gap:8 }}>
          <button className={styles.btnOutline} style={{ fontSize:12, padding:'4px 12px' }}
            onClick={() => { setEditando(e => !e); setFormEdit({ ...pauta, anuncianteId: pauta.anunciante?.id, formatoId: pauta.formato?.id, ubicacionId: pauta.ubicacion?.id, tipoContenidoId: pauta.tipoContenido?.id, cromatismoId: pauta.cromatismo?.id }); setDuplicando(false); }}>
            ✏️ {editando ? 'Cancelar edición' : 'Editar'}
          </button>
          <button className={styles.btnOutline} style={{ fontSize:12, padding:'4px 12px' }}
            onClick={() => { setDuplicando(d => !d); setEditando(false); }}>
            ⧉ {duplicando ? 'Cancelar' : 'Duplicar'}
          </button>
          <button className={styles.btnOutline} style={{ padding:'4px 12px', fontSize:12 }} onClick={onClose}>
            <X size={12} style={{ marginRight:4 }} />Cerrar
          </button>
        </div>
      </div>

      {/* Formulario de edición inline */}
      {editando && (
        <div style={{ background:'#fff', border:'1px solid #c9a227', borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:10 }}>
            {[
              ['Fecha inicio', 'fechaInicio', 'date'],
              ['Fecha fin', 'fechaFin', 'date'],
              ['Num. ediciones', 'numEdiciones', 'number'],
            ].map(([label, key, type]) => (
              <div key={key}>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>{label}</label>
                <input type={type} value={formEdit[key]||''} onChange={e => setFormEdit(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
              </div>
            ))}
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>Modalidad vigencia</label>
              <select value={formEdit.modalidadVigencia||'fechas'} onChange={e => setFormEdit(f => ({ ...f, modalidadVigencia: e.target.value }))}
                style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
                <option value='fechas'>Por fechas</option>
                <option value='ediciones'>Por ediciones</option>
                <option value='indefinida'>Indefinida</option>
              </select>
            </div>
          </div>
          {/* Segunda fila: ubicación y tarifa */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>
                Ubicación <span style={{ color:'#c9a227', fontSize:11 }}>(define dónde aparece el banner)</span>
              </label>
              <select value={formEdit.ubicacionId||''} onChange={e => setFormEdit(f => ({ ...f, ubicacionId: Number(e.target.value) }))}
                style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
                <option value=''>-- Sin cambiar --</option>
                {anunciantes.length === 0 ? null : config?.ubicaciones?.map(u => (
                  <option key={u.id} value={u.id} disabled={u.canal === 'impreso'}>
                    {u.canal === 'impreso' ? '🔒 ' : ''}{u.nombre} {u.slugPosicion ? `[${u.slugPosicion}]` : u.canal === 'impreso' ? '(impreso — próximamente)' : '(sin slug)'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>Tarifa base</label>
              <select value={formEdit.tarifaBaseId||''} onChange={e => setFormEdit(f => ({ ...f, tarifaBaseId: e.target.value ? Number(e.target.value) : null }))}
                style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
                <option value=''>-- Sin cambiar --</option>
                {config?.tarifas?.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre} — ${t.valorCop?.toLocaleString('es-CO')}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className={styles.btnPrimary} disabled={saving} onClick={async () => {
              setSaving(true);
              try {
                await publicidadService.actualizarPauta(pauta.id, {
                  anuncianteId: pauta.anunciante?.id,
                  formatoId: pauta.formato?.id,
                  ubicacionId: formEdit.ubicacionId || pauta.ubicacion?.id,
                  tipoContenidoId: pauta.tipoContenido?.id,
                  cromatismoId: pauta.cromatismo?.id,
                  recargoEdicionId: formEdit.recargoEdicionId ?? pauta.recargoEdicion?.id ?? null,
                  tarifaBaseId: formEdit.tarifaBaseId ?? pauta.tarifaBaseId ?? null,
                  unidadesMedida: formEdit.unidadesMedida ?? null,
                  modalidadVigencia: formEdit.modalidadVigencia || pauta.modalidadVigencia || 'fechas',
                  numEdiciones: formEdit.numEdiciones ? Number(formEdit.numEdiciones) : pauta.numEdiciones,
                  fechaInicio: formEdit.fechaInicio || pauta.fechaInicio || null,
                  fechaFin: formEdit.fechaFin || pauta.fechaFin || null,
                  observaciones: pauta.observaciones,
                });
                await reload(); setEditando(false);
              } catch(e) { alert('Error: ' + e.message); }
              setSaving(false);
            }}>
              {saving ? 'Guardando...' : 'Guardar cambios y recalcular'}
            </button>
          </div>
        </div>
      )}

      {/* Duplicar pauta */}
      {duplicando && (
        <div style={{ background:'#fff', border:'1px solid #1B4332', borderRadius:8, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:13, marginBottom:10 }}>
            Se creará una copia de esta pauta con el mismo formato, ubicación, vigencia y tarifa. Solo cambia el anunciante.
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <select value={anuncianteDup} onChange={e => setAnuncianteDup(e.target.value)}
              style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
              <option value=''>-- Selecciona el nuevo anunciante --</option>
              {anunciantes.map(a => <option key={a.id} value={a.id}>{a.razonSocial}</option>)}
            </select>
            <button className={styles.btnPrimary} disabled={!anuncianteDup || saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await publicidadService.duplicarPauta(pauta.id, Number(anuncianteDup));
                  setDuplicando(false); setAnuncianteDup('');
                  alert('Pauta duplicada correctamente. Búscala en la lista.');
                  onClose();
                } catch(e) { alert('Error: ' + e.message); }
                setSaving(false);
              }}>
              {saving ? '...' : 'Crear duplicado'}
            </button>
          </div>
        </div>
      )}

      {/* Info básica en grid 4 cols */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16, fontSize:13 }}>
        <div><strong>Formato:</strong><br/>{pauta.formato?.nombre}</div>
        <div><strong>Ubicación:</strong><br/>{pauta.ubicacion?.nombre}</div>
        <div><strong>Tipo:</strong><br/>{pauta.tipoContenido?.nombre}</div>
        <div><strong>Canal:</strong><br/>{pauta.formato?.canal}</div>
        <div><strong>Vigencia:</strong><br/>
          {pauta.modalidadVigencia === 'ediciones' ? `${pauta.numEdiciones} ediciones`
           : pauta.modalidadVigencia === 'fechas' ? (() => {
               const r = precioCalculado();
               return `${pauta.fechaInicio} → ${pauta.fechaFin || '∞'}${r ? ` (${r.dias} días)` : ''}`;
             })()
           : 'Indefinida'}
        </div>
        <div><strong>Cromatismo:</strong><br/>{pauta.cromatismo?.nombre || '—'}</div>
        <div><strong>Estado:</strong><br/>
          <span className={`${styles.badge} ${ESTADO_COLORS[pauta.estado] || ''}`}>{pauta.estado}</span>
        </div>
      </div>

      {/* Aviso si precio es 0 por falta de tarifa */}
      {(!pauta.totalCop || pauta.totalCop === 0) && (
        <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:6, padding:'8px 12px', marginBottom:12, fontSize:13, color:'#92400e' }}>
          <strong>ⓘ Precio pendiente de calcular.</strong> Esta pauta no tiene tarifa base asignada.
          Usa el <strong>Cotizador</strong> para calcular el precio y luego crea una nueva pauta con la tarifa correcta,
          o actualiza manualmente el estado cuando el precio esté acordado con el anunciante.
        </div>
      )}

      {/* Desglose precio */}
      <div style={{ background:'#f0fdf4', border:'1px solid #a7f3d0', borderRadius:8, padding:14, marginBottom:16 }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>Desglose de precio</span>
          <button className={styles.btnOutline} style={{ fontSize:11, padding:'3px 10px' }}
            onClick={async () => {
              setSaving(true);
              try {
                await publicidadService.recalcularPrecio(pautaId);
                await reload();
              } catch(e) { alert('Error al recalcular: ' + e.message); }
              setSaving(false);
            }} disabled={saving}>
            ↻ Recalcular
          </button>
        </div>
        {[
          ['Precio base', pauta.precioBaseCop],
          ['+ Recargo ubicación', pauta.recargoUbicacionCop],
          ['+ Recargo cromatismo', pauta.recargoCromatismoCop],
          ['+ Recargo edición', pauta.recargoEdicionCop],
          ['- Descuento agencia', pauta.descuentoAgenciaCop ? -pauta.descuentoAgenciaCop : 0],
          ['+ IVA', pauta.ivaCop],
          ['Total', pauta.totalCop],
        ].map(([label, val]) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid rgba(0,0,0,0.05)', fontSize:13, fontWeight: label === 'Total' ? 700 : 400 }}>
            <span>{label}</span><span>{fmt(val)}</span>
          </div>
        ))}
      </div>

      {/* Cambiar estado */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:16, flexWrap:'wrap' }}>
        <label style={{ fontSize:12, fontWeight:600, color:'#555' }}>
          Estado <InfoTip text="Flujo: borrador → pendiente_material → en_revision → aprobada → activa. Solo las pautas activas con material aprobado aparecen en el periódico." />
        </label>
        <select value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)}
          style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
          {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <input placeholder='Observaciones...' value={obs} onChange={e => setObs(e.target.value)}
          style={{ flex:1, minWidth:160, padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }} />
        <button className={styles.btnPrimary} onClick={cambiarEstado} disabled={saving}>
          {saving ? '...' : 'Guardar estado'}
        </button>
      </div>

      {/* Material */}
      <div style={{ borderTop:'1px solid #eee', paddingTop:14, marginBottom:14 }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
          <Upload size={13} />Material publicitario
          <InfoTip text="Sube el archivo del aviso. El tipo de archivo debe coincidir con el formato contratado." />
        </div>

        {/* Especificaciones técnicas del formato */}
        {specsFormato() && (
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'8px 12px', marginBottom:10, fontSize:12, color:'#1e40af' }}>
            <strong>Especificaciones para "{pauta.formato?.nombre}":</strong>
            <ul style={{ margin:'4px 0 0 16px', padding:0 }}>
              {specsFormato().map(s => <li key={s}>{s}</li>)}
            </ul>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'120px 180px 220px 1fr auto', gap:8, alignItems:'flex-end', marginBottom:10 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>Tipo de archivo</label>
            <select value={materialTipo} onChange={e => setMaterialTipo(e.target.value)}
              style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
              {['imagen','video','audio','html','pdf'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>Nombre del material</label>
            <input placeholder='Ej: Banner principal' value={materialNombre} onChange={e => setMaterialNombre(e.target.value)}
              style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>
              URL de destino al hacer clic
            </label>
            <input placeholder='https://sitiodelanunciante.com' value={materialUrlDestino} onChange={e => setMaterialUrlDestino(e.target.value)}
              style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:3 }}>
              {['imagen','video','audio'].includes(materialTipo) ? 'Seleccionar archivo' : 'URL del archivo en Storage'}
            </label>
            {['imagen','video','audio'].includes(materialTipo) ? (
              <input type='file'
                accept={materialTipo === 'imagen' ? 'image/*' : materialTipo === 'video' ? 'video/mp4,video/webm' : 'audio/mpeg,audio/aac,audio/ogg'}
                onChange={e => setArchivoFile(e.target.files[0])}
                style={{ fontSize:12, padding:'4px 0' }} />
            ) : (
              <input placeholder='gs://... o https://...' value={materialUrl} onChange={e => setMaterialUrl(e.target.value)}
                style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #ddd', fontSize:13, boxSizing:'border-box' }} />
            )}
          </div>
          <button className={styles.btnPrimary} onClick={subirMaterial} disabled={saving || uploadProgress > 0} style={{ alignSelf:'flex-end' }}>
            {uploadProgress > 0 ? `Subiendo ${uploadProgress}%...` : 'Agregar'}
          </button>
        </div>

        {pauta.materiales?.length > 0 ? (
          <table className={styles.table}>
            <thead><tr><th>Nombre</th><th>Tipo</th><th>Estado</th><th>Principal</th><th>Acciones</th></tr></thead>
            <tbody>
              {pauta.materiales.filter(m => m.estadoRevision !== 'archivado').map(m => (
                <tr key={m.id}>
                  <td>{m.nombre}</td>
                  <td>{m.tipoArchivo}</td>
                  <td><span className={`${styles.badge} ${ESTADO_COLORS[m.estadoRevision] || styles.badgePendiente}`}>{m.estadoRevision}</span></td>
                  <td>{m.esPrincipal ? '⭐' : <button className={styles.btnIcon} onClick={async () => { await publicidadService.marcarPrincipal(pautaId, m.id); await reload(); }}>Marcar</button>}</td>
                  <td>
                    <div className={styles.actions}>
                      {m.estadoRevision === 'pendiente' && <>
                        <button className={styles.btnIcon} title='Aprobar' onClick={async () => { await publicidadService.revisarMaterial(m.id, { estadoRevision:'aprobado', observacionesRevision:'' }); await reload(); }}><CheckCircle size={12} /></button>
                        <button className={`${styles.btnIcon} ${styles.danger}`} title='Rechazar' onClick={async () => { const o = prompt('Motivo:'); if(o!==null){ await publicidadService.revisarMaterial(m.id, { estadoRevision:'rechazado', observacionesRevision:o }); await reload(); } }}><XCircle size={12} /></button>
                      </>}
                      <a href={m.urlStorage} target='_blank' rel='noreferrer' className={styles.btnIcon}>Ver</a>
                      {!m.esPrincipal && (
                        <button className={`${styles.btnIcon} ${styles.danger}`}
                          title={m.estadoRevision === 'pendiente' || m.estadoRevision === 'rechazado' ? 'Eliminar' : 'Archivar'}
                          onClick={async () => {
                            const accion = m.estadoRevision === 'pendiente' || m.estadoRevision === 'rechazado' ? 'eliminar' : 'archivar';
                            if (!window.confirm(`¿${accion === 'eliminar' ? 'Eliminar' : 'Archivar'} este material? ${accion === 'eliminar' ? 'Se borrará permanentemente.' : 'Quedará en el historial y se podrá reutilizar.'}`)) return;
                            await publicidadService.eliminarMaterial(m.id);
                            await reload();
                          }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className={styles.empty}>Sin material activo. Sube un archivo.</div>}

        {/* Historial de material archivado */}
        {pauta.materiales?.some(m => m.estadoRevision === 'archivado') && (
          <HistorialMaterial
            materiales={pauta.materiales.filter(m => m.estadoRevision === 'archivado')}
            pautaId={pautaId}
            onReactivar={reload}
          />
        )}
      </div>

      {/* Programar en edición */}
      <div style={{ borderTop:'1px solid #eee', paddingTop:14 }}>
        <div style={{ fontWeight:600, fontSize:13, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
          <Calendar size={13} />Programar en edición
          <InfoTip text="Asigna esta pauta a una o varias ediciones. Aparecerá automáticamente cuando la pauta esté ACTIVA y tenga material APROBADO. Como las ediciones son regionales, aparecerá en cada región de las ediciones asignadas." />
        </div>

        {/* Chips de ediciones ya asignadas */}
        {edicionesAsignadas.length > 0 ? (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
            {edicionesAsignadas.map(a => {
              const region = [a.municipioNombre, a.departamentoNombre].filter(Boolean).join(', ');
              const label = `Nº ${a.numero}${a.titulo ? ' — ' + a.titulo : ''}${region ? ' · ' + region : ''}`;
              return (
                <span key={a.pautaEdicionId} style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  background:'#e8f2ec', color:'#1B4332', border:'1px solid #cfe2d6',
                  padding:'4px 8px', borderRadius:14, fontSize:12,
                }}>
                  {label}
                  <button
                    title="Quitar de esta edición"
                    onClick={async () => {
                      if (!window.confirm(`¿Quitar la pauta de la edición "${label}"?`)) return;
                      try {
                        await publicidadService.desactivarProgramacion(a.pautaEdicionId);
                        await reloadAsignadas();
                      } catch(e) { alert('Error: ' + e.message); }
                    }}
                    style={{
                      background:'transparent', border:'none', color:'#1B4332',
                      cursor:'pointer', padding:0, fontSize:14, lineHeight:1,
                    }}>×</button>
                </span>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize:12, color:'#888', marginBottom:10 }}>
            No está asignada a ninguna edición todavía.
          </div>
        )}

        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select value={edicionSel} onChange={e => setEdicionSel(e.target.value)}
            style={{ flex:1, padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
            <option value=''>-- Selecciona una edición --</option>
            {ediciones
              .filter(e => !edicionesAsignadas.some(a => a.edicionId === e.id))
              .map(e => (
                <option key={e.id} value={e.id}>
                  {e.title || `Edición Nº ${e.number}`}{e.publishedAt ? ` — ${new Date(e.publishedAt).toLocaleDateString('es-CO')}` : ''}
                </option>
              ))}
          </select>
          <button className={styles.btnPrimary} disabled={!edicionSel || programando}
            onClick={async () => {
              setProgramando(true);
              try {
                await publicidadService.programarEnEdicion({ pautaId, edicionId: edicionSel, materialId: pauta.materiales?.find(m => m.esPrincipal)?.id || null, orden: 0 });
                setEdicionSel('');
                await reloadAsignadas();
              } catch(e) { alert('Error: ' + e.message); }
              setProgramando(false);
            }}>
            {programando ? '...' : 'Asignar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Formulario de nueva pauta (colapsable) ───────────────────
function FormNuevaPauta({ config, plantillas, onCreada, onCancelar }) {
  const [plantillaSel, setPlantillaSel] = useState('');
  const [form, setForm] = useState({ modalidadVigencia:'fechas', numEdiciones:1 });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  const sf = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Si cambia a vigencia por fechas, limpiar unidades (se calculan solas)
      if (k === 'modalidadVigencia' && v === 'fechas') next.unidadesMedida = null;
      return next;
    });
  };

  const aplicarPlantilla = (plantillaId) => {
    setPlantillaSel(plantillaId);
    if (!plantillaId) return;
    const pl = plantillas.find(p => String(p.id) === String(plantillaId));
    if (!pl) return;
    // Pre-rellenar formato de la plantilla
    sf('formatoId', pl.formatoId);
  };

  const crear = async () => {
    if (!form.anuncianteId || !form.formatoId || !form.ubicacionId || !form.tipoContenidoId) {
      return alert('Completa los campos obligatorios: Anunciante, Formato, Ubicación y Tipo de contenido.');
    }
    setSaving(true);
    try {
      await publicidadService.crearPauta(form);
      onCreada();
    } catch(e) { alert('Error: ' + e.message); }
    setSaving(false);
  };

  const fieldStyle = { display:'flex', flexDirection:'column', gap:4 };
  const labelStyle = { fontSize:11, fontWeight:600, color:'#555', textTransform:'uppercase', display:'flex', alignItems:'center', gap:4 };
  const inputStyle = { padding:'7px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13, width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ border:'1px solid #c9a227', borderRadius:8, marginBottom:16, background:'#fffdf5' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px',
        cursor:'pointer', borderBottom: open ? '1px solid #eee' : 'none' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontWeight:600, fontSize:14, color:'#1B4332' }}>
          <Plus size={14} style={{ marginRight:6, verticalAlign:'middle' }} />Nueva Pauta
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {open && (
        <div style={{ padding:16 }}>
          {/* Plantilla */}
          <div style={{ marginBottom:16, padding:12, background:'#f0fdf4', borderRadius:8 }}>
            <label style={{ ...labelStyle, marginBottom:6 }}>
              Plantilla de pauta
              <InfoTip text="Elige una plantilla para pre-rellenar los campos más comunes. Luego puedes ajustar cualquier valor." />
            </label>
            <select value={plantillaSel} onChange={e => aplicarPlantilla(e.target.value)} style={{ ...inputStyle, maxWidth:400 }}>
              <option value=''>— Sin plantilla (configurar manualmente) —</option>
              {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>

          {/* Grid 4 columnas — fila 1 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Anunciante *</label>
              <select value={form.anuncianteId||''} onChange={e => sf('anuncianteId', Number(e.target.value))} style={inputStyle}>
                <option value=''>-- Selecciona --</option>
                {config.anunciantes.map(a => <option key={a.id} value={a.id}>{a.razonSocial}</option>)}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Formato *
                <InfoTip text="Dimensiones físicas del aviso. El formato determina el tamaño del archivo que debe entregar el anunciante." />
              </label>
              <select value={form.formatoId||''} onChange={e => sf('formatoId', Number(e.target.value))} style={inputStyle}>
                <option value=''>-- Selecciona --</option>
                {config.formatos.map(f => (
                  <option key={f.id} value={f.id} disabled={f.canal === 'impreso'}>
                    {f.canal === 'impreso' ? '🔒 ' : ''}{f.nombre} ({f.canal}){f.canal === 'impreso' ? ' — próximamente' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Ubicación *
                <InfoTip text="Dónde aparece el aviso en el periódico. Cada posición tiene un recargo diferente sobre la tarifa base." />
              </label>
              <select value={form.ubicacionId||''} onChange={e => sf('ubicacionId', Number(e.target.value))} style={inputStyle}>
                <option value=''>-- Selecciona --</option>
                {config.ubicaciones.map(u => (
                  <option key={u.id} value={u.id} disabled={u.canal === 'impreso'}>
                    {u.canal === 'impreso' ? '🔒 ' : ''}{u.nombre} (+{u.recargoPct}%){u.canal === 'impreso' ? ' — próximamente' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Tipo de contenido *
                <InfoTip text="Aviso comercial, edicto, publirreportaje, etc. Los edictos tienen tarifa fija regulada." />
              </label>
              <select value={form.tipoContenidoId||''} onChange={e => sf('tipoContenidoId', Number(e.target.value))} style={inputStyle}>
                <option value=''>-- Selecciona --</option>
                {config.tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}{t.tarifaFijaCop ? ` ($${t.tarifaFijaCop.toLocaleString('es-CO')} fijo)` : ''}</option>)}
              </select>
            </div>
          </div>

          {/* Grid 4 columnas — fila 2 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Tarifa base
                <InfoTip text="Precio por unidad de medida. Para pautas por días: precio diario. Para CPM: precio por cada 1.000 impresiones." />
              </label>
              <select value={form.tarifaBaseId||''} onChange={e => sf('tarifaBaseId', e.target.value ? Number(e.target.value) : null)} style={inputStyle}>
                <option value=''>-- Sin tarifa --</option>
                {config.tarifas.map(t => {
                  const esImpreso = t.nombre?.toLowerCase().includes('impres') || t.nombre?.toLowerCase().includes('módulo') || t.nombre?.toLowerCase().includes('modulo') || t.nombre?.toLowerCase().includes('cm/col');
                  return (
                    <option key={t.id} value={t.id} disabled={esImpreso}>
                      {esImpreso ? '🔒 ' : ''}{t.nombre} — ${t.valorCop.toLocaleString('es-CO')}{esImpreso ? ' — próximamente' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            {form.modalidadVigencia !== 'fechas' && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Unidades de medida
                <InfoTip text="Para CPM: miles de impresiones (ej: 50 = 50.000 impresiones). Para ediciones: número de ediciones. No aplica cuando la vigencia es por fechas." />
              </label>
              <input type='number' min='1' value={form.unidadesMedida||''} onChange={e => sf('unidadesMedida', e.target.value ? Number(e.target.value) : null)}
                placeholder='Ej: 50' style={inputStyle} />
            </div>
            )}
            {form.modalidadVigencia === 'fechas' && (
            <div style={{ ...fieldStyle, background:'#f0fdf4', borderRadius:6, padding:'8px 10px', justifyContent:'center', alignItems:'center' }}>
              <label style={{ ...labelStyle, color:'#065f46', marginBottom:2 }}>Días calculados</label>
              <span style={{ fontSize:22, fontWeight:700, color:'#1B4332' }}>
                {form.fechaInicio && form.fechaFin
                  ? Math.ceil((new Date(form.fechaFin) - new Date(form.fechaInicio)) / (1000*60*60*24)) + 1
                  : '—'}
              </span>
              <span style={{ fontSize:11, color:'#555' }}>días · el backend los calcula automáticamente</span>
            </div>
            )}
            <div style={fieldStyle}>
              <label style={labelStyle}>Cromatismo
                <InfoTip text="Full Color agrega 40% sobre la tarifa base. Blanco y Negro no tiene recargo." />
              </label>
              <select value={form.cromatismoId||''} onChange={e => sf('cromatismoId', e.target.value ? Number(e.target.value) : null)} style={inputStyle}>
                <option value=''>-- Sin especificar --</option>
                {config.cromatismo.map(c => <option key={c.id} value={c.id}>{c.nombre} (+{c.recargoPct}%)</option>)}
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Recargo edición
                <InfoTip text="Las ediciones especiales o dominicales tienen mayor audiencia y por eso se cobra un recargo adicional." />
              </label>
              <select value={form.recargoEdicionId||''} onChange={e => sf('recargoEdicionId', e.target.value ? Number(e.target.value) : null)} style={inputStyle}>
                <option value=''>-- Ninguno --</option>
                {config.recargosEd.map(r => {
                  const esImpreso = r.nombre?.toLowerCase().includes('dominical') || r.nombre?.toLowerCase().includes('lanzamiento');
                  return (
                    <option key={r.id} value={r.id} disabled={esImpreso}>
                      {esImpreso ? '🔒 ' : ''}{r.nombre} (+{r.recargoPct}%){esImpreso ? ' — próximamente' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Grid 4 columnas — fila 3: vigencia */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Modalidad de vigencia
                <InfoTip text="Por días: el anunciante paga X días de aparición. Por fechas: aparece entre dos fechas específicas. Por ediciones: aparece en N ediciones del periódico." />
              </label>
              <select value={form.modalidadVigencia} onChange={e => sf('modalidadVigencia', e.target.value)} style={inputStyle}>
                <option value='fechas'>Por rango de fechas</option>
                <option value='ediciones'>Por número de ediciones</option>
                <option value='indefinida'>Indefinida</option>
              </select>
            </div>
            {form.modalidadVigencia === 'ediciones' && (
              <div style={fieldStyle}>
                <label style={labelStyle}>Número de ediciones</label>
                <input type='number' min='1' value={form.numEdiciones} onChange={e => sf('numEdiciones', Number(e.target.value))} style={inputStyle} />
              </div>
            )}
            {form.modalidadVigencia === 'fechas' && <>
              <div style={fieldStyle}>
                <label style={labelStyle}>Fecha inicio</label>
                <input type='date' value={form.fechaInicio||''} onChange={e => sf('fechaInicio', e.target.value)} style={inputStyle} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Fecha fin</label>
                <input type='date' value={form.fechaFin||''} onChange={e => sf('fechaFin', e.target.value)} style={inputStyle} />
              </div>
            </>}
            <div style={{ ...fieldStyle, gridColumn: form.modalidadVigencia === 'indefinida' ? 'span 3' : undefined }}>
              <label style={labelStyle}>Observaciones</label>
              <input value={form.observaciones||''} onChange={e => sf('observaciones', e.target.value)}
                placeholder='Notas internas sobre esta pauta...' style={inputStyle} />
            </div>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className={styles.btnOutline} onClick={onCancelar}>Cancelar</button>
            <button className={styles.btnPrimary} onClick={crear} disabled={saving}>
              {saving ? 'Creando...' : 'Crear pauta'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB PRINCIPAL ────────────────────────────────────────────
export default function PautasTab() {
  const [pautas, setPautas]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [detalle, setDetalle]     = useState(null);
  const [creando, setCreando]     = useState(false);
  const [msg, setMsg]             = useState(null);
  const [config, setConfig]       = useState({ anunciantes:[], formatos:[], ubicaciones:[], cromatismo:[], recargosEd:[], tipos:[], tarifas:[] });
  const [plantillas, setPlantillas] = useState([]);

  const load = async (estado) => {
    setLoading(true);
    setPautas(await publicidadService.getPautas(estado || null));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    Promise.all([
      publicidadService.getAnunciantes(),
      publicidadConfigService.getFormatos(),
      publicidadConfigService.getUbicaciones(),
      publicidadConfigService.getCromatismo(),
      publicidadConfigService.getRecargosEdicion(),
      publicidadConfigService.getTiposContenido(),
      publicidadConfigService.getTarifas(),
      publicidadConfigService.getPlantillas(),
    ]).then(([anunciantes, formatos, ubicaciones, cromatismo, recargosEd, tipos, tarifas, plts]) => {
      setConfig({ anunciantes, formatos, ubicaciones, cromatismo, recargosEd, tipos, tarifas });
      setPlantillas(plts || []);
    });
  }, []);

  const eliminarBorrador = async (id) => {
    if (!window.confirm('¿Eliminar esta pauta en borrador? Esta acción no se puede deshacer.')) return;
    await publicidadService.cambiarEstado(id, { estado: 'cancelada', observaciones: 'Eliminada por el administrador' });
    await load(filtroEstado);
  };

  return (
    <div>
      {/* Cabecera con filtro y botón */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12 }}>
        <strong style={{ fontSize:15 }}>Pautas</strong>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <select value={filtroEstado} onChange={e => { setFiltroEstado(e.target.value); load(e.target.value); }}
            style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', fontSize:13 }}>
            <option value=''>Todos los estados</option>
            {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <button className={styles.btnPrimary} onClick={() => { setCreando(c=>!c); setMsg(null); setDetalle(null); }}>
            {creando ? <ChevronUp size={14} /> : <Plus size={14} />}
            {creando ? 'Cancelar' : 'Nueva pauta'}
          </button>
        </div>
      </div>

      {msg && <div className={`${styles.msg} ${msg.type==='ok'?styles.msgOk:styles.msgError}`}>{msg.text}</div>}

      {/* Formulario colapsable */}
      {creando && (
        <FormNuevaPauta
          config={config}
          plantillas={plantillas}
          onCreada={async () => { setCreando(false); await load(filtroEstado); setMsg({ type:'ok', text:'Pauta creada en estado borrador.' }); }}
          onCancelar={() => setCreando(false)}
        />
      )}

      {/* Detalle expandible */}
      {detalle && <DetallePauta pautaId={detalle} onClose={() => setDetalle(null)} config={config} />}

      {/* Tabla */}
      {loading ? (
        <div className={styles.empty}>Cargando…</div>
      ) : pautas.length === 0 ? (
        <div className={styles.empty}>No hay pautas{filtroEstado ? ` con estado "${filtroEstado}"` : ''}.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>#</th><th>Anunciante</th><th>Formato</th><th>Ubicación</th><th>Vigencia</th><th>Total</th><th>Estado</th><th></th></tr>
          </thead>
          <tbody>
            {pautas.map(p => <PautaRow key={p.id} pauta={p} onVerDetalle={id => { setDetalle(id); setCreando(false); }} onEliminar={eliminarBorrador} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}
